import {BehaviorSubject} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {Injectable, inject} from '@angular/core';

import {API_NOTIFICATIONS_GROUPED, API_NOTIFICATIONS_MARK_BY_TYPE} from '../../config/urls';
import {NotificationViewModel} from '../models/notifications.view-model';
import {NotificationConverterService} from '@shared/services/notification-converter.service';

interface NotificationGroupDto {
  type: string;
  count: number;
}

@Injectable({providedIn: 'root'})
export class NotificationsService {

  private readonly subject = new BehaviorSubject<NotificationViewModel[]>([]);
  private notificationsVMs: NotificationViewModel[] = [];
  public readonly notifications = this.subject.asObservable();
  
  private isLoading = false; // Prevent duplicate concurrent API calls

  private readonly http = inject(HttpClient);
  private readonly notificationConverterService = inject(NotificationConverterService);

  loadNotifications(includeRead: boolean = false): void {
    // Prevent duplicate concurrent API calls
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    
    const url = `${API_NOTIFICATIONS_GROUPED}?includeRead=${includeRead}`;
    
    this.http.get<NotificationGroupDto[]>(url).subscribe({
      next: (groupedNotifications) => {
        // API returns pre-grouped data - just convert to view models
        this.notificationsVMs = groupedNotifications
          .map(group => this.notificationConverterService.convertGroupToViewModel(group.type, group.count))
          .filter(vm => vm !== null); // Filter out null results from unknown notification types
        
        this.subject.next([... this.notificationsVMs]);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('[Notifications] Error loading notifications:', error);
        this.isLoading = false;
      }
    });
  }

  markNotificationTypeAsRead(notificationType: string): void {
    // Mark all notifications of this type as read
    // The API will handle marking all notifications of this type for the current user
    
    // Optimistic update: Remove immediately from UI
    const originalNotifications = [...this.notificationsVMs];
    this.notificationsVMs = this.notificationsVMs.filter(
      notification => notification.type !== notificationType
    );
    this.subject.next([...this.notificationsVMs]);
    
    // Make API call
    const url = `${API_NOTIFICATIONS_MARK_BY_TYPE}?type=${notificationType}`;
    
    this.http.put<void>(url, null).subscribe({
      next: () => {
        // Successfully marked as read
      },
      error: (error) => {
        console.error('Error marking notifications as read:', error);
        // Restore notifications on error
        this.notificationsVMs = originalNotifications;
        this.subject.next([...this.notificationsVMs]);
      }
    });
  }
}
